
import React, { FormEvent, useState} from 'react';
import BrandLogo from "./BrandLogo";
import { useTheme } from "../theme/ThemeContext";
import { logEvent } from '../utils/analytics';
import { UserPlus, X, Send, Loader2 } from "lucide-react";
import { db } from "../firebase/config"; // Ensure your firebase config is exported
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import BetaTesterModal from "./BetaTesterModal";



interface TesterFormData {
    name: string;
    email: string;
    phone: string;
}

export default function Navbar() {
    const { isDark, toggleTheme } = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [formData, setFormData] = useState<TesterFormData>({
        name: '',
        email: '',
        phone: ''
    });

    const [errorMessage, setErrorMessage] = useState<string | null>(null);


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage(null);

        const emailId = formData.email.toLowerCase().trim();
        const testerRef = doc(db, "beta_testers", emailId);

        try {
            await setDoc(testerRef, {
                ...formData,
                email: emailId,
                timestamp: serverTimestamp(),
                source: 'navbar_cta'
            });

            logEvent('Conversion', 'Beta_Sign_Up_Success', 'Navbar');
            setSubmitted(true);

            setTimeout(() => {
                setIsModalOpen(false);
                setSubmitted(false);
                setFormData({ name: '', email: '', phone: '' });
            }, 3000);

        } catch (error: any) {
            if (error.code === 'permission-denied') {
                setErrorMessage("You're already on our tester list, or signup is not allowed right now.");
            } else if (error.code === 'unavailable') {
                setErrorMessage("Network error. Please check your connection.");
            } else {
                setErrorMessage("Something went wrong. Please try again later.");
            }

            console.error("Firestore Error:", error.code, error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const [isTesterModalOpen, setIsTesterModalOpen] = useState(false);

    const handleBecomeTesterClick = () => {
        logEvent('Conversion', 'Open_Tester_Modal', 'Navbar_CTA');
        setIsTesterModalOpen(true);
    };


    return (
        <>
            <nav className="sticky top-0 z-50 border-b border-[var(--color-brand-border)] bg-[var(--color-brand-background)]/78 backdrop-blur-xl">
                {/* Gradient Decorations */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-r from-[var(--color-brand-primary)]/6 via-transparent to-[var(--color-brand-primary)]/4" />
                </div>

                <div className="relative mx-auto flex max-w-6xl items-center justify-between px-6 py-1.5">
                    <a href="/" className="group flex items-center rounded-xl px-1 py-0.5 transition-all">
                        <div className="scale-90 origin-left transition-transform group-hover:scale-[0.92]">
                            <BrandLogo />
                        </div>
                    </a>

                    <div className="hidden items-center gap-6 text-xs font-bold text-[var(--color-brand-text-secondary)] md:flex">
                        <a href="#features" className="hover:text-[var(--color-brand-primary)]">Features</a>
                        <a href="#about" className="hover:text-[var(--color-brand-primary)]">About</a>
                        <a href="#pricing" className="hover:text-[var(--color-brand-primary)]">Pricing</a>

                        <div className="mx-1 h-4 w-px bg-[var(--color-brand-border)]" />

                        {/* New Early Access CTA */}
                        <button
                            onClick={handleBecomeTesterClick}
                            className="group inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[var(--color-brand-primary)] to-cyan-500 px-4 py-2 text-xs font-black text-white shadow-md shadow-[var(--color-brand-primary)]/20 transition-all hover:scale-[1.02] hover:brightness-110"
                        >
                            <UserPlus size={16} className="transition-transform group-hover:rotate-12" />
                            Join the Waitlist
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <button onClick={toggleTheme} className="rounded-lg border border-[var(--color-brand-border)] bg-[var(--color-brand-card)]/80 px-3 py-1.5 text-[10px] font-black">
                            {isDark ? "LIGHT" : "DARK"}
                        </button>
                    </div>
                </div>
            </nav>

            <BetaTesterModal
                isOpen={isTesterModalOpen}
                onClose={() => setIsTesterModalOpen(false)}
            />
        </>
    );
}